import { TCollabThread, TiptapCollabProvider } from "@tiptap-pro/provider";
import { subscribeToThreads, hoverOffThread, hoverThread } from "@tiptap-pro/extension-comments";
import { Editor } from "@tiptap/react";
import React, { useCallback, useRef } from "react";

interface UseThreadsReturn {
  threads: TCollabThread[];
  selectedThread: string | null;
  setSelectedThread: (
    threadId: string | null | ((currentThreadId: string | null) => string | null),
  ) => void;
  selectThreadInEditor: (threadId: string) => void;
  deleteThread: (threadId: string) => void;
  resolveThread: (threadId: string) => void;
  unresolveThread: (threadId: string) => void;
  updateComment: (
    threadId: string,
    commentId: string,
    content: string,
    metaData: Record<string, unknown> | undefined,
  ) => void;
  onHoverThread: (threadId: string) => void;
  onLeaveThread: () => void;
}

export const useThreads = (
  provider: TiptapCollabProvider,
  editor: Editor | null,
): UseThreadsReturn => {
  const [threads, setThreads] = React.useState<TCollabThread[]>([]);
  const [selectedThread, setSelectedThread] = React.useState<string | null>(null);

  const setSelectedThreadWrapper = useCallback(
    (threadId: string | null | ((currentThreadId: string | null) => string | null)) => {
      if (typeof threadId === "function") {
        setSelectedThread(threadId);
      } else {
        setSelectedThread(threadId);
      }
    },
    [],
  );
  const threadsRef = useRef<TCollabThread[]>([]);

  React.useEffect(() => {
    if (provider) {
      const unsubscribe = subscribeToThreads({
        provider,
        callback: (currentThreads) => {
          setThreads(currentThreads);
          threadsRef.current = currentThreads;
        },
      });

      return () => {
        unsubscribe();
      };
    }
  }, [provider]);

  const selectThreadInEditor = useCallback(
    (threadId: string) => {
      editor?.chain().selectThread({ id: threadId }).run();
    },
    [editor],
  );

  const deleteThread = useCallback(
    (threadId: string) => {
      provider.deleteThread(threadId);
      editor?.commands.removeThread({ id: threadId });
    },
    [editor, provider],
  );

  const resolveThread = useCallback(
    (threadId: string) => {
      editor?.commands.resolveThread({ id: threadId });
    },
    [editor],
  );

  const unresolveThread = useCallback(
    (threadId: string) => {
      editor?.commands.unresolveThread({ id: threadId });
    },
    [editor],
  );

  const updateComment = useCallback(
    (
      threadId: string,
      commentId: string,
      content: string,
      metaData: Record<string, unknown> | undefined,
    ) => {
      editor?.commands.updateComment({
        threadId,
        id: commentId,
        content,
        data: metaData,
      });
    },
    [editor],
  );

  const onHoverThread = useCallback(
    (threadId: string) => {
      editor && hoverThread(editor, [Number(threadId)]);
    },
    [editor],
  );

  const onLeaveThread = useCallback(() => {
    editor && hoverOffThread(editor);
  }, [editor]);

  return {
    threads,
    selectedThread,
    setSelectedThread: setSelectedThreadWrapper,
    selectThreadInEditor,
    deleteThread,
    resolveThread,
    unresolveThread,
    updateComment,
    onHoverThread,
    onLeaveThread,
  };
};
